import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { readWeek, addDays, mondayOf } from "../lib/reviewerWeek";
import type { WeekTree } from "../lib/reviewerWeekVocabulary.generated";
import { MISSED_AFTER_DAYS, daysToReview, nameOr, type MyItem, type MyProposal, type MyReview, type RawSession, type ReviewerLink } from "../lib/reviewerSeat";

/* THE REVIEWER SEAT'S READS (step 2, 2026-10-10). The client's training comes ONLY through the three locked functions of
 * `20261010220000` (`reviewer_links`, `reviewer_week`, `reviewer_sessions` — they refuse anyone but an ACTIVE reviewer of
 * THIS client); his own rows (`reviewer_reviews`, `reviewer_review_items`, `reviewer_week_proposals`) through their RLS.
 * 🔴 No `coach_*` table is named here or anywhere in the seat (ruler `reviewerSeatNoCoachWrite.test.ts`).
 * A failed read is a failed page, never an empty one. */

export type Load<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; data: T };

/** `reviewer_links()` — every link this account is part of; `reload` after Accept / Leave. */
export function useReviewerLinks(): { state: Load<ReviewerLink[]>; reload: () => void } {
  const [state, setState] = useState<Load<ReviewerLink[]>>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const reload = useCallback(() => setSeq((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    void supabase.rpc("reviewer_links").then(({ data, error }) => {
      if (cancelled) return;
      setState(error !== null ? { status: "error", message: error.message } : { status: "ready", data: (data ?? []) as ReviewerLink[] });
    });
    return () => { cancelled = true; };
  }, [seq]);
  return { state, reload };
}

export async function acceptLink(id: string): Promise<string | null> {
  const r = await supabase.rpc("reviewer_accept", { p_link: id });
  return r.error === null ? null : r.error.message;
}
export async function endLink(id: string): Promise<string | null> {
  const r = await supabase.rpc("reviewer_end", { p_link: id });
  return r.error === null ? null : r.error.message;
}

/** The written week(s) from `from` to `to` (≤ 62 days), as the ONE vocabulary's tree — refused shape = a failed read. */
export async function fetchWeek(clientId: string, from: string, to: string): Promise<{ week: WeekTree; error: null } | { week: null; error: string }> {
  const r = await supabase.rpc("reviewer_week", { p_client: clientId, p_from: from, p_to: to });
  if (r.error !== null) return { week: null, error: r.error.message };
  // The checker's window is a Monday week; a two-week read is checked as two.
  const all = (r.data ?? []) as unknown[];
  const mondays = [...new Set((all as { scheduled_date?: string }[]).map((d) => d.scheduled_date ?? ""))];
  for (const d of mondays) {
    const monday = mondayOfSafe(d);
    if (monday === null || readWeek(all.filter((x) => mondayOfSafe((x as { scheduled_date?: string }).scheduled_date ?? "") === monday), monday) === null) {
      return { week: null, error: "The week came back in a shape this page does not know." };
    }
  }
  return { week: all as WeekTree, error: null };
}
const mondayOfSafe = (iso: string): string | null => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? mondayOf(iso) : null);

export async function fetchSessions(clientId: string, from: string, to: string): Promise<{ sessions: RawSession[]; error: null } | { sessions: null; error: string }> {
  const r = await supabase.rpc("reviewer_sessions", { p_client: clientId, p_from: from, p_to: to });
  if (r.error !== null) return { sessions: null, error: r.error.message };
  return { sessions: (r.data ?? []) as RawSession[], error: null };
}

const REVIEW_COLS = "id, session_id, scheduled_date, body, sent_at, audio_path, audio_seconds, video_path, video_seconds, photo_path";
const ITEM_COLS = "id, review_id, workout_session_exercise_id, position, exercise_name, body, suggested, audio_path, audio_seconds, video_path, video_seconds, photo_path";
const PROPOSAL_COLS = "id, week_start, week, note, sent_at, seen_at, updated_at";

/** One client's page: the finished days of the last four weeks, his reviews of them, his proposed weeks. */
export type ReviewerClientData = { sessions: RawSession[]; week: WeekTree; reviews: MyReview[]; proposals: MyProposal[] };
export function useReviewerClient(clientId: string, today: string): { state: Load<ReviewerClientData>; reload: () => void } {
  const [state, setState] = useState<Load<ReviewerClientData>>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const reload = useCallback(() => setSeq((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    void (async () => {
      const s = await fetchSessions(clientId, addDays(today, -27), today);
      if (cancelled) return;
      if (s.sessions === null) return setState({ status: "error", message: s.error });
      // The written weeks over the same four, and the next one (the days' names; the weeks he can propose).
      const w = await fetchWeek(clientId, mondayOf(addDays(today, -27)), addDays(mondayOf(today), 13));
      if (cancelled) return;
      if (w.week === null) return setState({ status: "error", message: w.error });
      const [rv, pr] = await Promise.all([
        supabase.from("reviewer_reviews").select(REVIEW_COLS).eq("client_id", clientId),
        supabase.from("reviewer_week_proposals").select(PROPOSAL_COLS).eq("client_id", clientId).order("week_start", { ascending: false }),
      ]);
      if (cancelled) return;
      const err = rv.error ?? pr.error;
      if (err !== null) return setState({ status: "error", message: err.message });
      setState({ status: "ready", data: { sessions: s.sessions, week: w.week, reviews: (rv.data ?? []) as MyReview[], proposals: (pr.data ?? []) as MyProposal[] } });
    })();
    return () => { cancelled = true; };
  }, [clientId, today, seq]);
  return { state, reload };
}

/** The Dashboard's `Reviewing` rows: each client he ACTIVELY reviews and the days still open to him (`daysToReview`) —
 *  `reviewer_links()`, then per client `reviewer_sessions` over the open window (a week) and his own reviews of it. */
export type ReviewingDue = { clientId: string; name: string; due: number };
export function useReviewingDue(today: string): Load<ReviewingDue[]> {
  const links = useReviewerLinks().state;
  const [state, setState] = useState<Load<ReviewingDue[]>>({ status: "loading" });
  useEffect(() => {
    if (links.status !== "ready") { setState(links); return; }
    let cancelled = false;
    const from = addDays(today, -MISSED_AFTER_DAYS);
    const active = links.data.filter((l) => l.role === "reviewer" && l.status === "active");
    void Promise.all(active.map(async (l): Promise<ReviewingDue | string> => {
      const s = await fetchSessions(l.client_id, from, today);
      if (s.sessions === null) return s.error;
      const rv = await supabase.from("reviewer_reviews").select("session_id, sent_at").eq("client_id", l.client_id).gte("scheduled_date", from);
      if (rv.error !== null) return rv.error.message;
      return { clientId: l.client_id, name: nameOr(l.other_name), due: daysToReview(s.sessions, rv.data ?? [], today) };
    })).then((rows) => {
      if (cancelled) return;
      const failed = rows.find((r): r is string => typeof r === "string");
      setState(failed !== undefined ? { status: "error", message: failed }
        : { status: "ready", data: (rows as ReviewingDue[]).sort((a, b) => a.name.localeCompare(b.name)) });
    });
    return () => { cancelled = true; };
  }, [links, today]);
  return state;
}

/** One day's review: the session, the written week around it (this + next), his draft or sent review and its items. */
export type ReviewerDayData = { session: RawSession; week: WeekTree; review: MyReview | null; items: MyItem[]; clientName: string };
export function useReviewerDay(clientId: string, sessionId: string, clientName: string): { state: Load<ReviewerDayData | null>; reload: () => void } {
  const [state, setState] = useState<Load<ReviewerDayData | null>>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const reload = useCallback(() => setSeq((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const fail = (message: string) => { if (!cancelled) setState({ status: "error", message }); };
      // The day first: the session is found in a 62-day read ending today (the function's cap), then the weeks around it.
      const today = new Date().toISOString().slice(0, 10);
      const s = await fetchSessions(clientId, addDays(today, -62), today);
      if (cancelled) return;
      if (s.sessions === null) return fail(s.error);
      const session = s.sessions.find((x) => x.id === sessionId);
      if (session === undefined || session.scheduled_date === null) return setState({ status: "ready", data: null });
      const day = session.scheduled_date.slice(0, 10);
      const monday = mondayOfSafe(day) ?? day;
      const w = await fetchWeek(clientId, monday, addDays(monday, 13));
      if (cancelled) return;
      if (w.week === null) return fail(w.error);
      const rv = await supabase.from("reviewer_reviews").select(REVIEW_COLS).eq("client_id", clientId).eq("session_id", sessionId).maybeSingle();
      if (cancelled) return;
      if (rv.error !== null) return fail(rv.error.message);
      const review = (rv.data ?? null) as MyReview | null;
      let items: MyItem[] = [];
      if (review !== null) {
        const it = await supabase.from("reviewer_review_items").select(ITEM_COLS).eq("review_id", review.id).order("position");
        if (cancelled) return;
        if (it.error !== null) return fail(it.error.message);
        items = (it.data ?? []) as MyItem[];
      }
      setState({ status: "ready", data: { session, week: w.week, review, items, clientName } });
    })();
    return () => { cancelled = true; };
  }, [clientId, sessionId, clientName, seq]);
  return { state, reload };
}

/** The proposed week's page: the written week (Monday … Sunday) and his proposal for it, if any. */
export type ReviewerWeekData = { written: WeekTree; proposal: MyProposal | null };
export function useReviewerWeek(clientId: string, monday: string): { state: Load<ReviewerWeekData>; reload: () => void } {
  const [state, setState] = useState<Load<ReviewerWeekData>>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const reload = useCallback(() => setSeq((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const w = await fetchWeek(clientId, monday, addDays(monday, 6));
      if (cancelled) return;
      if (w.week === null) return setState({ status: "error", message: w.error });
      const p = await supabase.from("reviewer_week_proposals").select(PROPOSAL_COLS).eq("client_id", clientId).eq("week_start", monday).maybeSingle();
      if (cancelled) return;
      if (p.error !== null) return setState({ status: "error", message: p.error.message });
      setState({ status: "ready", data: { written: w.week, proposal: (p.data ?? null) as MyProposal | null } });
    })();
    return () => { cancelled = true; };
  }, [clientId, monday, seq]);
  return { state, reload };
}
